import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { submitLead } from "../../lib/api";
import { Field, inputCls, Honeypot } from "./fields";
import { CTAButton } from "../common/CTAButton";
import { projectTypes, constructionServicesList } from "../../data/content";

const schema = z.object({
    name: z.string().min(2, "Enter your name"),
    company: z.string().optional(),
    mobile: z.string().min(8, "Enter a valid mobile number"),
    email: z.string().email("Enter a valid email"),
    project_type: z.string().optional(),
    location: z.string().optional(),
    project_size: z.string().optional(),
    service: z.string().optional(),
    start_date: z.string().optional(),
    description: z.string().optional(),
    honeypot: z.string().optional(),
});

export const ConstructionForm = () => {
    const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm({ resolver: zodResolver(schema) });

    const onSubmit = async (values) => {
        try {
            await submitLead("construction", values);
            toast.success("Construction enquiry submitted. Our team will reach out soon.");
            reset();
        } catch (e) {
            toast.error("Something went wrong. Please try again.");
        }
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} data-testid="construction-form" className="space-y-5">
            <Honeypot register={register} />
            <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Name" required error={errors.name?.message}>
                    <input className={inputCls} placeholder="Your name" data-testid="cn-name" {...register("name")} />
                </Field>
                <Field label="Company">
                    <input className={inputCls} placeholder="Company name" data-testid="cn-company" {...register("company")} />
                </Field>
                <Field label="Mobile Number" required error={errors.mobile?.message}>
                    <input className={inputCls} placeholder="+91" data-testid="cn-mobile" {...register("mobile")} />
                </Field>
                <Field label="Email" required error={errors.email?.message}>
                    <input className={inputCls} placeholder="name@company.com" data-testid="cn-email" {...register("email")} />
                </Field>
                <Field label="Project Type">
                    <select className={inputCls} data-testid="cn-type" defaultValue="" {...register("project_type")}>
                        <option value="" disabled>Select type</option>
                        {projectTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                </Field>
                <Field label="Project Location">
                    <input className={inputCls} placeholder="City / State" data-testid="cn-location" {...register("location")} />
                </Field>
                <Field label="Estimated Project Size">
                    <input className={inputCls} placeholder="e.g. Area / Budget range" data-testid="cn-size" {...register("project_size")} />
                </Field>
                <Field label="Required Service">
                    <select className={inputCls} data-testid="cn-service" defaultValue="" {...register("service")}>
                        <option value="" disabled>Select service</option>
                        {constructionServicesList.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                </Field>
                <Field label="Expected Start Date">
                    <input type="date" className={inputCls} data-testid="cn-date" {...register("start_date")} />
                </Field>
            </div>
            <Field label="Project Description">
                <textarea rows={4} className={inputCls} placeholder="Describe your construction project" data-testid="cn-description" {...register("description")} />
            </Field>
            <CTAButton type="submit" variant="primary" arrow disabled={isSubmitting} data-testid="cn-submit" className="w-full sm:w-auto disabled:opacity-60">
                {isSubmitting ? "Submitting..." : "Request Construction Quote"}
            </CTAButton>
        </form>
    );
};
